<template>
  <div class="campaign-detail-page">
    <div class="campaign-detail-header">
      <h1 class="campaign-detail-title">
        Kampanya Detayı
      </h1>
    </div>

    <CampaignStatus
      v-if="showCampaignStatus"
      :status="participationStatus"
      :campaign-definitions="campaignDefinitions"
      :status-data="statusData"
      :is-gmv-limit-used="isGmvLimitUsed"
      :is-order-limit-used="isOrderLimitUsed"
      :show-validity-bar="showValidityPeriod"
    />

    <div class="campaign-detail-card">
      <div class="campaign-detail-card-header">
        <div class="campaign-detail-card-title-badge-wrapper">
          <div class="campaign-detail-card-title-wrapper">
            <div class="campaign-detail-card-icon">
              <Icons
                name="CampaignIcon"
                size="lg"
              />
            </div>
            <span class="campaign-detail-card-label">Kampanya Bilgisi</span>
          </div>
          <div
            v-if="showUrgencyBadge"
            class="campaign-detail-status-badge"
            :style="{
              backgroundColor: urgencyBadge.colorCode,
              borderColor: urgencyBadge.colorCode,
            }"
          >
            <span
              class="badge-text"
              :style="{ color: urgencyBadge.textColorCode }"
            >{{ urgencyBadge.title }}</span>
          </div>
        </div>
        <button
          v-if="!showCampaignStatus"
          class="campaign-detail-join-button"
          @click="joinCampaign"
        >
          <span class="button-text">Kampanyaya Katıl</span>
        </button>
      </div>

      <div class="campaign-detail-body">
        <div class="campaign-detail-row campaign-name">
          <div class="label">
            Kampanya Adı
          </div>
          <div class="value">
            {{ campaignData.campaignName }}
          </div>
        </div>

        <div
          class="campaign-detail-grid"
          :class="{ 'grid-4-cols': showCampaignStatus }"
        >
          <div class="campaign-detail-cell">
            <div class="label">
              Kampanya Katılım Tarih Aralığı
            </div>
            <div class="value">
              {{ campaignData.startDate }} - {{ campaignData.endDate }}
            </div>
          </div>
          <div
            v-if="showCampaignStatus"
            class="campaign-detail-cell"
          >
            <div class="label">
              {{
                isStatusPlanned(participationStatus)
                  ? 'Planlama Tarihi'
                  : 'Katıldığınız Tarih'
              }}
            </div>
            <div class="value">
              {{
                isStatusPlanned(participationStatus)
                  ? statusData.plannedDate
                  : campaignData.joinDate
              }}
            </div>
          </div>
          <div class="campaign-detail-cell">
            <div class="label">
              Komisyon İndirimi
            </div>
            <div class="value">
              {{ campaignData.commissionDiscount }}
            </div>
          </div>
          <div
            v-if="showValidityPeriod"
            class="campaign-detail-cell"
          >
            <div class="label">
              Geçerlilik Süresi
            </div>
            <div class="value">
              {{ campaignData.validityPeriod }}
            </div>
          </div>
          <div
            v-if="isNotNull(campaignData.totalSalesLimit)"
            class="campaign-detail-cell"
          >
            <div class="label">
              Kampanya Toplam Satış Limiti (₺)
            </div>
            <div class="value">
              {{ campaignData.totalSalesLimit }}
            </div>
          </div>
          <div
            v-if="isNotNull(campaignData.orderLimit)"
            class="campaign-detail-cell"
          >
            <div class="label">
              Kampanya Sipariş Limiti (Adet)
            </div>
            <div class="value">
              {{ campaignData.orderLimit }}
            </div>
          </div>
          <div class="campaign-detail-cell">
            <div class="label">
              Kampanya ID
            </div>
            <div class="value">
              {{ campaignId }}
            </div>
          </div>
        </div>

        <div
          v-if="campaignData.validCategories.length > 0"
          class="campaign-detail-row"
        >
          <div class="label">
            Geçerli Kategoriler:
          </div>
          <div
            v-if="isAllCategories"
            class="value"
          >
            Tüm Kategoriler
          </div>
          <div
            v-else
            class="value"
          >
            {{ campaignData.validCategories.join(', ') }}
          </div>
        </div>

        <div
          v-if="campaignData.invalidCategories.length > 0"
          class="campaign-detail-row"
        >
          <div class="label">
            Geçerli Olmayan Kategoriler:
          </div>
          <div class="value">
            {{ campaignData.invalidCategories.join(', ') }}
          </div>
        </div>

        <div class="campaign-detail-info-box">
          <div class="info-content-container">
            <div class="info-title-wrapper">
              <div class="info-title">
                Kampanya koşulları nelerdir?
              </div>
            </div>
            <ul>
              <li class="info-item">
                Kategori komisyon indirimi, yalnızca kampanya kapsamında adı
                geçen kategorilerde/ürün gruplarında geçerlidir.
              </li>
              <li class="info-item info-item-rest">
                Geçerlilik süresi, toplam satış veya sipariş adedi limitlerinden
                herhangi birine ulaşıldığında kampanya sona erecektir.
              </li>
              <li class="info-item info-item-rest">
                n11, gerekli gördüğü durumlarda oluşturulan kampanyaları tek
                taraflı olarak durdurma veya sonlandırma hakkını saklı tutar.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <ModalJoinCampaign
      ref="joinModalRef"
      @join-success="onJoinSuccessCampaign"
      @join-dismiss="onJoinDismissCampaign"
      @join-close="onJoinCloseClickCampaign"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { Icons } from '@n11/so-ui-components';
import { formatTimestamp } from '@/utils/formatedDate';
import { formatNumberBasic } from '@/utils/formatNumber';
import {
  sendCategoryCommissionCampaignEvent,
  mapCampaignToGA4Params,
} from '@/utils/ga4/helper';
import { GA4_EVENT } from '@/utils/ga4/event';
import CampaignStatus from './partials/CampaignStatus';
import { useCampaignDetail } from './services';
import { URGENCY_BADGE_DEFAULTS, JOINED_STATUS_TYPES } from './constants';
import { ModalJoinCampaign } from './partials';

const route = useRoute();
const router = useRouter();
const store = useStore();
const joinModalRef = ref(null);

const {
  data: campaignDetailData,
  error: campaignDetailError,
  fetchData: fetchCampaignDetail,
} = useCampaignDetail();

const campaignId = computed(() => route.params.id);
const campaignDefinitions = computed(
  () => definition.value?.sellerStatusDefinitions || [],
);

const campaignData = ref({
  campaignName: '',
  startDate: '',
  endDate: '',
  joinDate: '',
  commissionDiscount: '',
  validityPeriod: '',
  totalSalesLimit: '',
  orderLimit: '',
  validCategories: [],
  invalidCategories: [],
});

const campaignStatus = ref('');
const participationStatus = ref('');
const definition = ref(null);
const isGmvLimitUsed = ref(true);
const isOrderLimitUsed = ref(true);
const showValidityPeriod = ref(true);

const isAllCategories = ref(false);

const showCampaignStatus = computed(() => !!participationStatus.value);
const sellerInfo = computed(() => ({
  sellerId: store.state.auth.user.sellerId || '',
  sellerName: store.state.auth.user.username || '',
}));

const isNotNull = (value) =>
  value !== null && value !== 'undefined' && Number(value) !== 0;

const statusData = ref({
  validityUsed: '0',
  validityTotal: '0',
  validityPercent: 0,
  salesUsed: '0',
  salesTotal: '0',
  salesPercent: 0,
  orderUsed: '0',
  orderTotal: '0',
  orderPercent: 0,
});

const isStatusPlanned = (status) => status === JOINED_STATUS_TYPES.PLANNED;

const mapCampaignDetail = (data) => {
  const sp = data.sellerParticipation;

  campaignData.value = {
    campaignName: data.name || '',
    startDate: formatTimestamp(data.startDate, {
      dateHourSeparator: ',',
      spaceAfterDate: false,
    }),
    endDate: formatTimestamp(data.endDate, {
      dateHourSeparator: ',',
      spaceAfterDate: false,
    }),
    joinDate: sp
      ? formatTimestamp(sp.joinDate, {
          dateHourSeparator: ',',
          spaceAfterDate: false,
        })
      : '',
    commissionDiscount: `%${data.commissionDiscountRate ?? ''}`,
    validityPeriod: `${data.validityDayCount ?? ''} Gün`,
    totalSalesLimit: formatNumberBasic(Number(data.defaultGmvLimit ?? 0)),
    orderLimit: formatNumberBasic(Number(data.defaultOrderLimit ?? 0)),
    validCategories: (data.includedCategories || []).map((c) => c.name),
    invalidCategories: (data.excludedCategories || []).map((c) => c.name),
  };

  isAllCategories.value = data.allCategoryIncluded || false;
  campaignStatus.value = data.urgencyBadge || '';
  definition.value = data.definition || null;
  isGmvLimitUsed.value = data.isGmvLimitUsed ?? true;
  isOrderLimitUsed.value = data.isOrderLimitUsed ?? true;
  showValidityPeriod.value = !!data.validityDayCount;

  if (sp) {
    participationStatus.value = sp.status || '';

    statusData.value = {
      validityUsed: String(sp.daysElapsed ?? 0),
      validityTotal: String(sp.totalDays ?? 0),
      validityPercent: sp.totalDays
        ? Math.round((sp.daysElapsed / sp.totalDays) * 100)
        : 0,
      salesUsed: formatNumberBasic(Number(sp.gmvUsage ?? 0)),
      salesTotal: formatNumberBasic(Number(sp.gmvLimit ?? 0)),
      salesPercent: sp.gmvUsagePercentage ?? 0,
      orderUsed: String(sp.orderUsage ?? 0),
      orderTotal: String(sp.orderLimit ?? 0),
      orderPercent: sp.orderUsagePercentage ?? 0,
      plannedDate: formatTimestamp(sp.plannedDate ?? '', {
        dateHourSeparator: ',',
        spaceAfterDate: false,
      }),
      joinDate: formatTimestamp(sp.joinDate ?? '', {
        dateHourSeparator: ',',
        spaceAfterDate: false,
      }),
      dateUsagePercentage: sp.dateUsagePercentage ?? 0,
    };
  } else {
    participationStatus.value = '';
  }
};

watch(campaignDetailData, (newData) => {
  if (newData) {
    mapCampaignDetail(newData);
  }
});

watch(campaignDetailError, (newError) => {
  if (!newError) return;

  const errorCode = newError?.data?.errors?.[0]?.code;
  if (errorCode === 'SELLER_COMMISSION_CAMPAIGN.campaignNotActive') {
    router.push({
      name: 'Home',
      query: { campaignEnded: 'true' },
    });
    return;
  }

  store.commit('common/SET_TOASTER_DATA', {
    type: 'error',
    title: '',
    description: 'Kampanya detayı yüklenirken bir hata oluştu.',
  });
});

watch(
  campaignId,
  (newId) => {
    if (newId) {
      fetchCampaignDetail(newId);
    }
  },
  { immediate: true },
);

const urgencyBadge = computed(() => {
  const code = campaignStatus.value;
  if (!code) return null;
  const apiDef = definition.value?.urgencyBadges?.find((b) => b.code === code);
  const fallback = URGENCY_BADGE_DEFAULTS[code];
  if (!apiDef && !fallback) return null;
  return {
    title: apiDef?.title || fallback?.title || '',
    colorCode: apiDef?.colorCode || fallback?.colorCode,
    textColorCode: apiDef?.textColorCode || fallback?.textColorCode,
  };
});

const showUrgencyBadge = computed(
  () => !showCampaignStatus.value && !!urgencyBadge.value,
);

const joinCampaign = async () => {
  sendCategoryCommissionCampaignEvent(GA4_EVENT.JOIN_CAMPAIGN_CLICK, {
    ...mapCampaignToGA4Params(campaignDetailData.value || {}, sellerInfo.value),
    page_type: 'Kategori Komisyon Kampanyaları',
    tab: 'Kampanya Detayı',
  });

  joinModalRef.value?.open?.({
    ...campaignDetailData.value,
    joinContext: {
      type: 'campaignId',
      value: campaignId.value,
    },
  });
};

const onJoinSuccessCampaign = () => {
  sendCategoryCommissionCampaignEvent(
    GA4_EVENT.JOIN_CAMPAIGN_SUCCESS_COMISSION,
    {
      ...mapCampaignToGA4Params(
        campaignDetailData.value || {},
        sellerInfo.value,
      ),
      page_type: 'Kategori Komisyon Kampanyaları',
      tab: 'Kampanya Detayı',
    },
  );

  fetchCampaignDetail(campaignId.value);
};

const onJoinDismissCampaign = () => {
  sendCategoryCommissionCampaignEvent(GA4_EVENT.JOIN_CAMPAIGN_CANCEL_CLICK, {
    ...mapCampaignToGA4Params(campaignDetailData.value || {}, sellerInfo.value),
    page_type: 'Kategori Komisyon Kampanyaları',
    tab: 'Kampanya Detayı',
  });
};

const onJoinCloseClickCampaign = () => {
  sendCategoryCommissionCampaignEvent(GA4_EVENT.JOIN_CAMPAIGN_CLOSE_CLICK, {
    ...mapCampaignToGA4Params(campaignDetailData.value || {}, sellerInfo.value),
    page_type: 'Kategori Komisyon Kampanyaları',
    tab: 'Kampanya Detayı',
  });
};
</script>

<style scoped lang="scss">
@import '@/assets/styles/variables';

.campaign-detail-page {
  font-family: 'Open Sans', sans-serif;
}

.campaign-detail-header {
  display: flex;
  align-items: center;
  align-self: stretch;
  padding: 16px 0;
  gap: 10px;
}

.campaign-detail-title {
  color: $colorN80;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.2px;
  line-height: 26px;
  text-align: center;
}

.campaign-detail-card {
  position: relative;
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  align-items: flex-start;
  padding: 24px;
  border-radius: 8px;
  margin-bottom: 30px;
  background: $colorN00;
  gap: 16px;
}

.campaign-detail-card-header {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 16px;
}

.campaign-detail-card-title-badge-wrapper {
  display: flex;
  align-items: center;
  gap: 16px;
}

.campaign-detail-card-title-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
}

.campaign-detail-card-icon {
  display: flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
}

.campaign-detail-card-label {
  color: $colorN80;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.1px;
  line-height: 22px;
}

.campaign-detail-status-badge {
  display: flex;
  height: 24px;
  align-items: center;
  padding: 2px 8px;
  border: 0.5px solid $colorN30;
  border-radius: 4px;
  gap: 4px;
  white-space: nowrap;

  .badge-text {
    font-size: 11px;
    font-weight: 600;
    line-height: 14px;
  }
}

.campaign-detail-join-button {
  position: absolute;
  top: 24px;
  right: 25px;
  display: flex;
  height: 48px;
  align-items: center;
  justify-content: center;
  padding: 14px 24px;
  border: none;
  border-radius: 6px;
  background: $colorN80;
  cursor: pointer;
  gap: 4px;
  white-space: nowrap;

  .button-text {
    color: $colorN00;
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.05px;
    line-height: 20px;
  }
}

.campaign-detail-body {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 16px;
}

.campaign-detail-grid {
  display: grid;
  width: 100%;
  gap: 16px 24px;
  grid-template-columns: repeat(3, 1fr);

  &.grid-4-cols {
    grid-template-columns: repeat(4, 1fr);
  }
}

.campaign-detail-cell {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  gap: 6px;

  .label,
  .value {
    overflow: hidden;
    align-self: stretch;
    letter-spacing: -0.05px;
    text-overflow: ellipsis;
  }

  .label {
    height: 18px;
    color: $colorN80;
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    white-space: nowrap;
  }

  .value {
    height: 20px;
    color: $colorN80;
    font-size: 16px;
    font-weight: 600;
    line-height: 20px;
    white-space: nowrap;
  }
}

.campaign-detail-row {
  .label {
    overflow: hidden;
    align-self: stretch;
    color: $colorN80;
    font-size: 14px;
    font-weight: 400;
    letter-spacing: -0.05px;
    line-height: 18px;
    text-overflow: ellipsis;
  }

  .value {
    align-self: stretch;
    color: $colorN80;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: -0.05px;
    line-height: 20px;
  }

  &.campaign-name {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    align-self: stretch;
    gap: 6px;

    .label {
      height: 18px;
      white-space: nowrap;
    }

    .value {
      display: -webkit-box;
      overflow: hidden;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 1;
      line-clamp: 1;
      text-overflow: ellipsis;
    }
  }

  &:not(.campaign-name) {
    .label {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 1;
      line-clamp: 1;
    }
  }
}

.campaign-detail-info-box {
  padding: 24px;
  border: 1px solid $colorB50;
  border-radius: 8px;
  background: $colorB05;

  .info-content-container,
  .info-title-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    align-self: stretch;
  }

  .info-content-container {
    gap: 8px;
  }

  .info-title-wrapper {
    gap: 6px;
  }

  .info-title {
    color: $black;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: -0.05px;
    line-height: 20px;
  }

  .info-item {
    flex: 1 0 0;
    color: $colorN80;
    font-size: 16px;
    font-weight: 400;
    letter-spacing: -0.05px;
    line-height: 20px;

    &.info-item-rest {
      align-self: stretch;
    }
  }

  ul {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    align-self: stretch;
    padding-left: 18px;
    margin: 0;
    gap: 8px;
    list-style-type: disc;

    li {
      margin: 0;
    }
  }
}
</style>
